import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { AuthService } from '../../core/services/auth.service';
import { OrganizationService } from '../../core/services/organization.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { User } from '../../core/models/user.model';
import { Organization, OrganizationCreate, OrganizationType } from '../../core/models/organization.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * Company Profile Component
 * Handles both creating and editing company/organization information
 */
@Component({
  selector: 'app-company-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective, MaxLengthDirective],
  templateUrl: './company-profile.component.html',
  styleUrls: ['./company-profile.component.scss']
})
export class CompanyProfileComponent implements OnInit {
  companyForm!: FormGroup;
  isEditMode = false;
  isLoading = false;
  currentUser: User | null = null;
  existingOrganization: Organization | null = null;
  errorMessage: string | null = null;

  // Organization type options
  organizationTypes: { value: OrganizationType; label: string }[] = [
    { value: OrganizationType.Individual, label: 'Individual' },
    { value: OrganizationType.Llc, label: 'LLC' },
    { value: OrganizationType.SCorp, label: 'S-Corp' },
    { value: OrganizationType.CCorp, label: 'C-Corp' },
    { value: OrganizationType.Partnership, label: 'Partnership' },
    { value: OrganizationType.SoleProprietorship, label: 'Sole Proprietorship' },
    { value: OrganizationType.NonProfit, label: 'Non-Profit' },
    { value: OrganizationType.Other, label: 'Other' },
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private organizationService: OrganizationService,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit(): void {
    // Subscribe to current user
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    this.initializeForm();
    this.loadExistingOrganization();
  }

  /**
   * Load existing organization if user has one
   */
  async loadExistingOrganization(): Promise<void> {
    try {
      this.isLoading = true;
      const organization = await this.organizationService.getMyOrganization();

      if (organization) {
        this.existingOrganization = organization;
        this.isEditMode = true;
        this.populateForm(organization);
      } else {
        this.isEditMode = false;
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      this.isEditMode = false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Populate form with existing organization data
   */
  populateForm(org: Organization): void {
    this.companyForm.patchValue({
      name: org.name,
      organizationType: org.organizationType,
      ein: org.ein || '',
      ssn: org.ssn || '',
      streetAddressLine1: org.streetAddressLine1,
      streetAddressLine2: org.streetAddressLine2 || '',
      city: org.city,
      state: org.state,
      zipCode: org.zipCode,
      country: org.country,
      email: org.email || '',
      phone: org.phone || ''
    });

    // Trigger validator update
    this.updateTaxIdValidators(org.organizationType);
  }

  initializeForm(): void {
    this.companyForm = this.fb.group({
      // Basic Information
      name: ['', Validators.required],

      // Organization Type & Tax Identification
      organizationType: ['', Validators.required],
      ein: [''],
      ssn: [''],

      // Address
      streetAddressLine1: ['', Validators.required],
      streetAddressLine2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', Validators.required],
      country: ['USA', Validators.required],

      // Optional Contact (separate from owner)
      email: ['', Validators.email],
      phone: ['']
    });

    // Update tax ID validators when organization type changes
    this.companyForm.get('organizationType')?.valueChanges.subscribe(type => {
      this.updateTaxIdValidators(type);
    });
  }

  updateTaxIdValidators(organizationType: OrganizationType | null | undefined): void {
    const einControl = this.companyForm.get('ein');
    const ssnControl = this.companyForm.get('ssn');

    if (organizationType === OrganizationType.Individual) {
      // Individual uses SSN
      ssnControl?.setValidators([Validators.required]);
      einControl?.clearValidators();
      einControl?.setValue('');
    } else if (organizationType) {
      // All other types use EIN
      einControl?.setValidators([Validators.required]);
      ssnControl?.clearValidators();
      ssnControl?.setValue('');
    } else {
      // No type selected yet
      einControl?.clearValidators();
      ssnControl?.clearValidators();
    }

    einControl?.updateValueAndValidity();
    ssnControl?.updateValueAndValidity();
  }

  get isIndividual(): boolean {
    return this.companyForm.get('organizationType')?.value === OrganizationType.Individual;
  }

  get hasOrganizationType(): boolean {
    return !!this.companyForm.get('organizationType')?.value;
  }

  async onSubmit(): Promise<void> {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    try {
      const formValue = this.companyForm.value;

      const organizationData: OrganizationCreate = {
        name: formValue.name,
        organizationType: formValue.organizationType,
        ownerProfileId: '', // Will be set by backend from auth token
        streetAddressLine1: formValue.streetAddressLine1,
        streetAddressLine2: formValue.streetAddressLine2 || undefined,
        city: formValue.city,
        state: formValue.state,
        zipCode: formValue.zipCode,
        country: formValue.country,
        ein: formValue.organizationType !== OrganizationType.Individual ? formValue.ein : undefined,
        ssn: formValue.organizationType === OrganizationType.Individual ? formValue.ssn : undefined,
        email: formValue.email || undefined,
        phone: formValue.phone || undefined
      };

      if (this.isEditMode && this.existingOrganization) {
        // Update existing organization
        await this.organizationService.updateOrganization(
          this.existingOrganization.id,
          organizationData
        );
      } else {
        // Create new organization
        await this.organizationService.createOrganization(organizationData);

        // Mark onboarding item as completed
        this.onboardingService.completeItem('create_company').subscribe();
      }

      // Navigate back to dashboard
      this.router.navigate(['/dashboard']);
    } catch (error: any) {
      console.error('Error saving organization:', error);
      this.errorMessage = error.message || 'Failed to save organization. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/dashboard']);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Company Profile' : 'Create Company Profile';
  }

  get submitButtonText(): string {
    return this.isEditMode ? 'Update Company' : 'Create Company';
  }

  get ownerDisplayName(): string {
    if (!this.currentUser) return '';
    return this.currentUser.displayName || this.currentUser.email || '';
  }

  get ownerEmail(): string {
    return this.currentUser?.email || '';
  }
}
